import { useEffect, useState } from "react";
import { ArrowRight } from "lucide-react";
import { useResolutions, useTasks } from "../api/hooks";
import type { CalEvent } from "../api/types";
import { MONTHS_LONG, WEEKDAYS_LONG, absMinutes, addDays, datePart, formatTime, minutesOf, nowIn, parts, weekday } from "../lib/date";
import { anchorOf, useUi } from "../state/ui";
import { useVisibleEvents } from "./views/common";
import { CollapsibleSection, KindMark, TaskQuickAdd, TaskRow, isOverdue, taskWhen } from "./TasksPage";
import { TaskChart } from "./TaskChart";
import { ResolutionQuickAdd, ResolutionRow } from "./ResolutionsPage";
import "./pages.css";

function greeting(minutes: number): string {
  if (minutes < 12 * 60) return "Bom dia";
  if (minutes < 18 * 60) return "Boa tarde";
  return "Boa noite";
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** Minutes since midnight in the user's timezone, ticking every 30 s so counts drop as things pass. */
function useNowMinutes(timeZone: string): number {
  const [minutes, setMinutes] = useState(() => nowIn(timeZone).minutes);
  useEffect(() => {
    const tick = () => setMinutes(nowIn(timeZone).minutes);
    tick();
    const t = setInterval(tick, 30_000);
    return () => clearInterval(t);
  }, [timeZone]);
  return minutes;
}

/**
 * Tasks and reminders still open for today (or overdue), what the summary counts and
 * the dark tile lists. Appointments are counted apart: they're on the agenda list.
 */
function openToDos(tasks: CalEvent[], today: string): CalEvent[] {
  return tasks
    .filter((t) => t.kind !== "event" && !t.completed && datePart(t.start) <= today)
    .sort((a, b) => Number(isOverdue(b, today)) - Number(isOverdue(a, today)) || Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));
}

/**
 * What's still ahead today. An appointment counts until it ends or is checked off; a
 * task or reminder until it's checked off.
 */
function remainingToday(events: CalEvent[], toDos: CalEvent[], done: Set<string>, today: string, nowAbs: number) {
  const open = events.filter((e) => !done.has(e.key));
  const eventsLeft = open.filter((e) => e.allDay || absMinutes(e.end) > nowAbs);
  const dueToday = toDos.filter((t) => !isOverdue(t, today));
  return {
    events: eventsLeft.length,
    tasks: dueToday.filter((t) => t.kind === "task").length,
    reminders: dueToday.filter((t) => t.kind === "reminder").length,
    overdue: toDos.length - dueToday.length,
    passed: events.length - eventsLeft.length
  };
}

function TodaySummary({ left }: { left: ReturnType<typeof remainingToday> }) {
  const parts = [
    left.events ? plural(left.events, "compromisso", "compromissos") : null,
    left.tasks ? plural(left.tasks, "tarefa", "tarefas") : null,
    left.reminders ? plural(left.reminders, "lembrete", "lembretes") : null
  ].filter((p): p is string => Boolean(p));
  const overdue = left.overdue ? <>, além de <strong>{plural(left.overdue, "pendência atrasada", "pendências atrasadas")}</strong></> : null;
  if (!parts.length) {
    if (overdue) return <p className="page-lead">Nada mais marcado para hoje{overdue}.</p>;
    return <p className="page-lead">{left.passed ? "Nada mais por hoje. Bom trabalho." : "Nenhum compromisso ou tarefa para hoje."}</p>;
  }
  const pendingWord = left.tasks || left.reminders ? (parts.length > 1 || left.tasks + left.reminders > 1 ? " pendentes" : " pendente") : "";
  return (
    <p className="page-lead">
      Hoje você {left.passed ? "ainda " : ""}tem{" "}
      {parts.map((p, i) => (
        <span key={p}>
          {i > 0 && (i === parts.length - 1 ? " e " : ", ")}
          <strong>{p}</strong>
        </span>
      ))}
      {pendingWord}
      {overdue}.
    </p>
  );
}

/** "em 2 h", "em 25 min", "agora". */
function untilLabel(e: CalEvent, nowAbs: number): string {
  const diff = absMinutes(e.start) - nowAbs;
  if (diff <= 0) return "agora";
  if (diff < 60) return `em ${diff} min`;
  const h = Math.round(diff / 60);
  return `em ${h} h`;
}

function timeRange(e: CalEvent, today: string): string {
  if (e.allDay || datePart(e.start) < today) return "Dia todo";
  return `${formatTime(minutesOf(e.start))}–${formatTime(minutesOf(e.end))}`;
}

/** Dark tile: what's next, then the rest of today as rule rows, then today's (and overdue) tasks and reminders. */
function TodayBand({ events, toDos }: { events: CalEvent[]; toDos: CalEvent[] }) {
  const ui = useUi();
  const now = nowIn(ui.me.timezone);
  const nowAbs = absMinutes(`${ui.today}T00:00`) + now.minutes;
  const timed = events.filter((e) => !e.allDay && datePart(e.start) === ui.today);
  const next = timed.find((e) => absMinutes(e.end) > nowAbs);
  const open = (e: CalEvent, el: Element) => ui.setOverlay({ kind: "details", event: e, anchor: anchorOf(el) });

  return (
    <div className="band" aria-label="Hoje" role="region">
      <div className="band-next">
        <span className="eyebrow">{next ? `A seguir · ${untilLabel(next, nowAbs)}` : "A seguir"}</span>
        {next ? (
          <button className="band-next-title display" onClick={(ev) => open(next, ev.currentTarget)}>
            {next.title || "(sem título)"}
          </button>
        ) : (
          <p className="band-next-title display">{events.length ? "Nada mais por hoje." : "Dia livre."}</p>
        )}
        {next && (
          <span className="band-next-meta">
            {timeRange(next, ui.today)}
            {next.location ? ` · ${next.location}` : ""}
          </span>
        )}
        <button className="band-link" onClick={ui.goToday}>
          Abrir agenda <ArrowRight size={16} />
        </button>
      </div>
      <div className="band-list">
        <span className="eyebrow">Hoje · {plural(events.length, "compromisso", "compromissos")}</span>
        {events.length ? (
          <ul>
            {events.map((e) => {
              const past = !e.allDay && absMinutes(e.end) <= nowAbs;
              return (
                <li key={e.key}>
                  <button className={`band-row${past ? " past" : ""}`} onClick={(ev) => open(e, ev.currentTarget)}>
                    <span className="band-time">{timeRange(e, ui.today)}</span>
                    <span className="band-title">{e.title || "(sem título)"}</span>
                    <span className="color-dot" style={{ background: e.color }} />
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="band-empty">Nenhum compromisso marcado.</p>
        )}
        {toDos.length > 0 && (
          <>
            <span className="eyebrow band-sub">Tarefas e lembretes · {toDos.length}</span>
            <ul>
              {toDos.map((t) => {
                const overdue = isOverdue(t, ui.today);
                return (
                  <li key={t.key}>
                    <button className="band-row" onClick={(ev) => open(t, ev.currentTarget)}>
                      <span className={`band-time${overdue ? " overdue" : ""}`}>{overdue ? taskWhen(t, ui.today) : t.allDay ? "Hoje" : formatTime(minutesOf(t.start))}</span>
                      <span className="band-title">{t.title || "(sem título)"}</span>
                      <KindMark kind={t.kind} />
                    </button>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
}

/** Start page as stacked tiles: centered headline, dark "today" tile, then tasks and resolutions cards on parchment. */
export function HomePage() {
  const ui = useUi();
  const { events } = useVisibleEvents(ui.today, addDays(ui.today, 1), { byCategory: false });
  const today = events.filter((e) => e.kind === "event").sort((a, b) => Number(b.allDay) - Number(a.allDay) || a.start.localeCompare(b.start));
  const taskLists = useTasks().data;
  const tasks = taskLists?.pending ?? [];
  const done = new Set((taskLists?.completed ?? []).map((t) => t.key));
  const nowMinutes = useNowMinutes(ui.me.timezone);
  const toDos = openToDos(tasks, ui.today);
  const left = remainingToday(today, toDos, done, ui.today, absMinutes(`${ui.today}T00:00`) + nowMinutes);
  const resolutions = useResolutions().data?.pending ?? [];
  const name = ui.me.name?.trim().split(/\s+/)[0];
  const { m, d } = parts(ui.today);

  return (
    <div className="page-scroll">
      <section className="tile">
        <header className="page wide page-head hero">
          <span className="eyebrow">
            {WEEKDAYS_LONG[weekday(ui.today)]} · {d} de {MONTHS_LONG[m - 1]}
          </span>
          <h1 className="page-hero display">
            {greeting(nowMinutes)}
            {name ? `, ${name}` : ""}.
          </h1>
          <TodaySummary left={left} />
        </header>
      </section>

      <section className="tile tile-dark">
        <div className="page wide">
          <TodayBand events={today} toDos={toDos} />
        </div>
      </section>

      <section className="tile tile-parchment">
        <div className="page wide home-cols">
          <section className="card">
            <div className="col-head">
              <h2 className="col-title display">Tarefas</h2>
              <button className="text-link" onClick={() => ui.setPage("tasks")}>
                Ver todas <ArrowRight size={14} />
              </button>
            </div>
            <TaskQuickAdd />
            {tasks.length ? (
              <ul className="rows">
                {tasks.slice(0, 5).map((t) => <TaskRow key={t.key} task={t} />)}
              </ul>
            ) : (
              <p className="empty">Tudo em dia.</p>
            )}
          </section>
          <section className="card">
            <div className="col-head">
              <h2 className="col-title display">Resoluções</h2>
              <button className="text-link" onClick={() => ui.setPage("resolutions")}>
                Ver todas <ArrowRight size={14} />
              </button>
            </div>
            <ResolutionQuickAdd />
            {resolutions.length ? (
              <ul className="rows">
                {resolutions.slice(0, 5).map((r) => <ResolutionRow key={r.id} r={r} />)}
              </ul>
            ) : (
              <p className="empty">Nada em aberto.</p>
            )}
          </section>
        </div>
        <div className="page wide home-chart">
          <CollapsibleSection title="Gráfico de concluídas" storageKey="home.chartOpen">
            <TaskChart />
          </CollapsibleSection>
        </div>
      </section>
    </div>
  );
}
